"use client";

import { useState, useEffect, useMemo } from "react";
import { format, startOfMonth, endOfMonth, startOfDay, endOfDay } from "date-fns";
import { Card, CardContent } from "../card";
import { Button } from "../button";
import { Badge } from "../badge";
import { ChevronLeft, ChevronRight, Calendar, List, Clock, CalendarDays, Plus, Settings, MoreHorizontal, Share, User, Download, Grid3X3, Eye, EyeOff } from "lucide-react";
import { useCalendarContext, CalendarViewType, CalendarProvider } from "../../lib/calendar-context";
import { downloadCSV, objectsToCSV } from "../../lib/csv-export";
import { useToast } from "../../hooks/use-toast";
import { AdvancedCalendar } from "./AdvancedCalendar";
import { EventList } from "./EventList";
import { FAQSection } from "./FAQSection";
import { ShareCalendarModal } from "./ShareCalendarModal";
import { AccountRequiredModal } from "../AccountRequiredModal";
import { usePreferences } from "../../lib/preferences-context";
import { useAccountRequired } from "../../hooks/useAccountRequired";
import { useMediaQuery } from "../../hooks/use-media-query";
import { CentralizedDrawer, useGlobalDrawerStack } from "../global-drawer-stack";
import { useGlobalModalStack } from "../global-modal-stack";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "../dropdown-menu";
// import { FloatingDebugModal } from "./FloatingDebugModal";
import { PersonSelector, CreateMeetingModal } from "../..";
import { CalendarMultiSelector } from "./CalendarMultiSelector";

interface UnifiedCalendarContentProps {
  tourControls?: React.ReactNode;
  // CRM Integration props
  onLogActivity?: (activityData: any) => Promise<void>;
  onComposeEmail?: (recipients: string[], subject?: string) => void;
  // Custom calendar component
  customCalendarComponent?: React.ComponentType<{
    events: any[];
    isLoading: boolean;
    onLogActivity?: (activityData: any) => Promise<void>;
    onComposeEmail?: (recipients: string[], subject?: string) => void;
  }>;
  // Bypass account requirement for testing
  bypassAccountCheck?: boolean;
}

function UnifiedCalendarContent({ 
  tourControls, 
  onLogActivity, 
  onComposeEmail,
  customCalendarComponent: CustomCalendar,
  bypassAccountCheck = false
}: UnifiedCalendarContentProps) {
  const [viewMode, setViewMode] = useState<"calendar" | "list">("calendar");
  const { preferences, updatePreferences } = usePreferences();
  const { hasAnyAccount, showAccountModal, closeModal } = useAccountRequired({
    showModal: !bypassAccountCheck,
  });
  const isMobile = useMediaQuery("(max-width: 768px)");
  const { openDrawer: openActionsDrawer, closeDrawer: closeActionsDrawer, isDrawerOpen } = useGlobalDrawerStack();
  const actionsDrawerOpen = isDrawerOpen("mobile-calendar-actions");
  const { openModal: openCreateMeetingModal, isModalOpen: isCreateMeetingOpen } = useGlobalModalStack();
  const { toast } = useToast();
  const [shareCalendarModalOpen, setShareCalendarModalOpen] = useState(false);
  
  // Calendar context hooks
  const {
    events,
    isLoading,
    error,
    currentDate,
    weekStart,
    weekEnd,
    currentView,
    setCurrentView,
    selectedPerson,
    setSelectedPerson,
    goToPreviousWeek,
    goToNextWeek,
    goToPreviousMonth,
    goToNextMonth,
    goToPreviousDay,
    goToNextDay,
    goToToday,
    calendarSources,
    sourceColors,
  } = useCalendarContext();

  // Export events to CSV
  const handleExportCSV = () => {
    if (!events || events.length === 0) {
      toast({
        title: "No events to export",
        description: "There are no events in the current view to export.",
        variant: "destructive",
      });
      return;
    }

    try {
      const csvContent = objectsToCSV(events);
      downloadCSV(csvContent, "calendar-events.csv");
      toast({
        title: "Export successful",
        description: "Calendar events have been exported to CSV.",
      });
    } catch (error) {
      console.error("Export error:", error);
      toast({
        title: "Export failed",
        description: "There was an error exporting the calendar events.",
        variant: "destructive",
      });
    }
  };
  

  // Set default view based on go mode preference
  useEffect(() => {
    if (preferences.goMode) {
      setViewMode("list");
    }
  }, [preferences.goMode]);

  // Dynamic navigation based on current view
  const navigationConfig = useMemo(() => {
    const monthStart = startOfMonth(currentDate);
    const monthEnd = endOfMonth(currentDate);
    const dayStart = startOfDay(currentDate);
    const dayEnd = endOfDay(currentDate);

    switch (currentView) {
      case "dayGridMonth":
        return {
          label: `${format(monthStart, "MMMM yyyy")}`,
          goToPrevious: goToPreviousMonth,
          goToNext: goToNextMonth,
          period: "month",
        };
      case "timeGridDay":
        return {
          label: format(currentDate, "EEEE, MMMM d, yyyy"),
          goToPrevious: goToPreviousDay,
          goToNext: goToNextDay,
          period: "day",
        };
      case "listWeek":
        return {
          label: `Week of ${format(weekStart, "MMM d")} - ${format(weekEnd, "MMM d, yyyy")}`,
          goToPrevious: goToPreviousWeek,
          goToNext: goToNextWeek,
          period: "week",
        };
      case "timeGridWeek":
      default:
        return {
          label: `Week of ${format(weekStart, "MMM d")} - ${format(weekEnd, "MMM d, yyyy")}`,
          goToPrevious: goToPreviousWeek,
          goToNext: goToNextWeek,
          period: "week",
        };
    }
  }, [currentView, currentDate, weekStart, weekEnd, goToPreviousMonth, goToNextMonth, goToPreviousDay, goToNextDay, goToPreviousWeek, goToNextWeek]);

  // Keyboard shortcuts for navigation
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Only handle shortcuts when not in an input field
      if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement) {
        return;
      }

      if (event.ctrlKey || event.metaKey) {
        switch (event.key) {
          case 'ArrowLeft':
            event.preventDefault();
            navigationConfig.goToPrevious();
            break;
          case 'ArrowRight':
            event.preventDefault();
            navigationConfig.goToNext();
            break;
          case 't':
            event.preventDefault();
            goToToday();
            break;
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [navigationConfig, goToToday]);

  // If no accounts are configured, show the account required modal (after all hooks)
  // Unless bypassAccountCheck is enabled for testing
  if (!hasAnyAccount && !bypassAccountCheck) {
    return (
      <div className="p-4 md:p-6">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold mb-2">Unified Calendar</h1>
          <p className="text-gray-600">
            Connect your calendar accounts to view your unified calendar
          </p>
          
          {/* Account Required Modal */}
          <AccountRequiredModal
            isOpen={showAccountModal}
            onClose={closeModal}
            title="Calendar Setup Required"
            description="You need to connect your calendar accounts to view your unified calendar."
          />
        </div>
      </div>
    );
  }

  // Show warning toast if individual calendars fail but others succeed
  useEffect(() => {
    if (error) {
      // Only show error if ALL calendars failed
      return;
    }
    
    // Check if any individual calendar failed but we still have data
    // This will be handled by showing a warning banner instead of blocking
  }, [error]);

  if (error) {
    return (
      <div className="p-4 md:p-6">
        <Card>
          <CardContent className="p-4 md:p-6">
            <div className="text-center text-destructive">
              Error loading calendar data. Please try again.
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 min-w-0 w-full p-4 md:p-6">
      {/* Warning Banner for Individual Calendar Failures */}
      {events && events.length > 0 && (
        <div className="space-y-2">
          {/* Show warnings if individual calendars failed but we still have data */}
        </div>
      )}
      
      {/* Header - Reorganized with clear hierarchy */}
      <div className="space-y-4 pb-6 border-b border-border">
        {/* Primary Header: Title + Actions */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Unified Calendar</h1>
          
          {/* Action Buttons - All aligned to the right */}
          <div className="flex items-center gap-2 w-full sm:w-auto flex-wrap sm:flex-nowrap">
            {/* Calendar Multi-Selector */}
            <CalendarMultiSelector
              disabled={isLoading}
              hideLabel={true}
              className="w-full sm:w-auto min-w-[180px]"
            />

            {/* View Toggle */}
            <div className="inline-flex items-center h-10 bg-muted/50 rounded-md p-1 border border-border">
              <Button
                variant={viewMode === "calendar" ? "default" : "ghost"}
                size="sm"
                onClick={() => setViewMode("calendar")}
                className="h-8 px-3"
              >
                <Calendar className="h-3.5 w-3.5 sm:mr-1.5" />
                <span className="hidden sm:inline text-xs">Calendar</span>
              </Button>
              <Button
                variant={viewMode === "list" ? "default" : "ghost"}
                size="sm"
                onClick={() => setViewMode("list")}
                className="h-8 px-3"
              >
                <List className="h-3.5 w-3.5 sm:mr-1.5" />
                <span className="hidden sm:inline text-xs">List</span>
              </Button>
            </div>

            <Button
              variant="outline"
              onClick={handleExportCSV}
              className="inline-flex items-center justify-center gap-2 h-10 px-4 flex-1 sm:flex-initial"
              disabled={isLoading}
            >
              <Download className="h-4 w-4" />
              <span className="hidden sm:inline">Export</span>
            </Button>
            <Button
              variant="outline"
              onClick={() => setShareCalendarModalOpen(true)}
              className="inline-flex items-center justify-center gap-2 h-10 px-4 flex-1 sm:flex-initial"
              disabled={isLoading}
            >
              <Share className="h-4 w-4" />
              <span className="hidden sm:inline">Share</span>
            </Button>
            <Button
              variant="outline"
              onClick={() => openCreateMeetingModal("create-meeting")}
              className="inline-flex items-center justify-center gap-2 h-10 px-4 flex-1 sm:flex-initial"
              disabled={isLoading}
            >
              <Plus className="h-4 w-4" />
              <span className="hidden sm:inline">Create</span>
            </Button>
            
            {/* More Actions - Desktop Dropdown / Mobile Drawer */}
            {isMobile ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => openActionsDrawer("mobile-calendar-actions")}
                className="h-10 px-3"
              >
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            ) : (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="h-10 px-3">
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-64">
                  <DropdownMenuLabel className="text-xs font-semibold text-muted-foreground uppercase">Calendar Views</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    onClick={() => setCurrentView("dayGridMonth")}
                    className="cursor-pointer"
                  >
                    <Grid3X3 className="h-4 w-4 mr-3" />
                    <span className="font-medium">Month View</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={() => setCurrentView("timeGridWeek")}
                    className="cursor-pointer"
                  >
                    <Calendar className="h-4 w-4 mr-3" />
                    <span className="font-medium">Week View</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={() => setCurrentView("timeGridDay")}
                    className="cursor-pointer"
                  >
                    <Clock className="h-4 w-4 mr-3" />
                    <span className="font-medium">Day View</span>
                  </DropdownMenuItem>
                  
                  <DropdownMenuSeparator />
                  <DropdownMenuLabel className="text-xs font-semibold text-muted-foreground uppercase">Actions</DropdownMenuLabel>
                  <DropdownMenuItem 
                    onClick={() => updatePreferences({ hideFAQ: !preferences.hideFAQ })}
                    className="cursor-pointer"
                  >
                    {preferences.hideFAQ ? (
                      <>
                        <Eye className="h-4 w-4 mr-3" />
                        <span className="font-medium">Show FAQ</span>
                      </>
                    ) : (
                      <>
                        <EyeOff className="h-4 w-4 mr-3" />
                        <span className="font-medium">Hide FAQ</span>
                      </>
                    )}
                  </DropdownMenuItem>
                  
                  {tourControls && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuLabel className="text-xs font-semibold text-muted-foreground uppercase">Help & Tours</DropdownMenuLabel>
                      <div className="px-2 py-1">
                        {tourControls}
                      </div>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Actions Drawer */}
      <CentralizedDrawer
        drawerId="mobile-calendar-actions"
        side="bottom"
        title="Calendar Actions"
        description="Additional calendar options and settings"
        showCloseButton={true}
        enableSwipeGesture={true}
      >
        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-6">
          <div className="space-y-3">
            <div className="space-y-1">
              <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Calendar Views</h3>
              <p className="text-sm text-muted-foreground">Switch between different display modes</p>
            </div>
            <Button
              variant={currentView === "dayGridMonth" ? "default" : "outline"}
              onClick={() => {
                setCurrentView("dayGridMonth");
                closeActionsDrawer("mobile-calendar-actions");
              }}
              className="w-full justify-start h-12"
            >
              <Grid3X3 className="h-4 w-4 mr-3" />
              <div className="text-left">
                <div className="font-medium">Month View</div>
                <div className="text-sm text-muted-foreground">See events by month</div>
              </div>
            </Button>
            
            <Button
              variant={currentView === "timeGridWeek" ? "default" : "outline"}
              onClick={() => {
                setCurrentView("timeGridWeek");
                closeActionsDrawer("mobile-calendar-actions");
              }}
              className="w-full justify-start h-12"
            >
              <Calendar className="h-4 w-4 mr-3" />
              <div className="text-left">
                <div className="font-medium">Week View</div>
                <div className="text-sm text-muted-foreground">See events by week</div>
              </div>
            </Button>
            
            <Button
              variant={currentView === "timeGridDay" ? "default" : "outline"}
              onClick={() => {
                setCurrentView("timeGridDay");
                closeActionsDrawer("mobile-calendar-actions");
              }}
              className="w-full justify-start h-12"
            >
              <Clock className="h-4 w-4 mr-3" />
              <div className="text-left">
                <div className="font-medium">Day View</div>
                <div className="text-sm text-muted-foreground">See events by day</div>
              </div>
            </Button>
          </div>

          {tourControls && (
            <div className="space-y-3">
              <div className="space-y-1">
                <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Help & Tours</h3>
                <p className="text-sm text-muted-foreground">Get help and guided tours</p>
              </div>
              {tourControls}
            </div>
          )}
          </div>
        </div>
      </CentralizedDrawer>

      {/* Main Content - Full width with proper FAQ sidebar */}
      <div className={`grid gap-6 ${preferences.hideFAQ ? "grid-cols-1" : "grid-cols-1 lg:grid-cols-3"} min-w-0 w-full`}>
        {/* Calendar/List View - Full width when no FAQ */}
        <div className={(preferences.hideFAQ ? "col-span-1" : "lg:col-span-2") + " min-w-0"}>
          <div className="w-full">
            {viewMode === "calendar" ? (
              CustomCalendar ? (
                <CustomCalendar 
                  events={events} 
                  isLoading={isLoading}
                  onLogActivity={onLogActivity}
                  onComposeEmail={onComposeEmail}
                />
              ) : (
                <AdvancedCalendar 
                  events={events} 
                  isLoading={isLoading}
                  onLogActivity={onLogActivity}
                  onComposeEmail={onComposeEmail}
                />
              )
            ) : (
              <EventList events={events} isLoading={isLoading} />
            )}
          </div>
        </div>

        {/* FAQ Section + Calendar Legend - Sidebar when enabled */}
        {!preferences.hideFAQ && (
          <div className="lg:col-span-1 min-w-0 space-y-6">
            <FAQSection />
            
            {/* Calendar Legend & Interaction Hints */}
            <Card className="bg-gradient-to-r from-muted/50 to-muted/30 border">
              <CardContent className="p-4">
                <div className="space-y-3">
                  {/* Calendar Sources */}
                  {calendarSources.length > 0 && (
                    <div className="space-y-2">
                      <div className="flex items-center gap-1.5">
                        <div className="w-1.5 h-1.5 rounded-full bg-primary"></div>
                        <span className="text-xs font-semibold text-foreground uppercase tracking-wider">Sources</span>
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        {calendarSources.map((source) => (
                          <div key={source.email} className="flex items-center gap-1.5 bg-background/80 rounded-md px-2 py-1 shadow-sm border">
                            <div 
                              className="w-2.5 h-2.5 rounded-full shadow-sm" 
                              style={{ backgroundColor: source.color }}
                            ></div>
                            <div className="flex items-center gap-1">
                              <span className="text-xs font-medium text-foreground truncate max-w-[150px]">
                                {source.email}
                              </span>
                              <span className="text-xs text-muted-foreground">({source.eventCount})</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Interaction Hints */}
                  <div className="space-y-2">
                    <div className="flex items-center gap-1.5">
                      <div className="w-1.5 h-1.5 rounded-full bg-primary"></div>
                      <span className="text-xs font-semibold text-foreground uppercase tracking-wider">Interaction</span>
                    </div>
                    <div className="space-y-1.5">
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                        <span>Click for details</span>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <div className="w-2 h-2 rounded-full bg-green-500"></div>
                        <span>Double-click to open</span>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      {/* Floating Debug Modal - Only in Development */}
      {/* <FloatingDebugModal isEnabled={true} /> */}
      
      {/* Account Required Modal */}
      <AccountRequiredModal
        isOpen={showAccountModal}
        onClose={closeModal}
        title="Calendar Setup Required"
        description="You need to connect your calendar accounts to view your unified calendar."
      />
      
      {/* Create Meeting Modal */}
      <CreateMeetingModal
        isOpen={isCreateMeetingOpen("create-meeting")}
        onClose={() => {
          // ResponsiveModal handles closing via global modal stack automatically
        }}
        initialDate={currentDate}
      />

      {/* Share Calendar Modal */}
      <ShareCalendarModal open={shareCalendarModalOpen} onOpenChange={setShareCalendarModalOpen} />

    </div>
  );
}

interface UnifiedCalendarPageProps {
  tourControls?: React.ReactNode;
  // CRM Integration props
  onLogActivity?: (activityData: any) => Promise<void>;
  onComposeEmail?: (recipients: string[], subject?: string) => void;
  // Custom calendar component
  customCalendarComponent?: React.ComponentType<{
    events: any[];
    isLoading: boolean;
    onLogActivity?: (activityData: any) => Promise<void>;
    onComposeEmail?: (recipients: string[], subject?: string) => void;
  }>;
  // Contact search props for attendee selection
  contacts?: Array<{ id: string; name: string; email: string; company_name?: string }>;
  onSearchContacts?: (searchTerm: string) => Promise<Array<{ id: string; name: string; email: string; company_name?: string }>>;
  isLoadingContacts?: boolean;
  // Bypass account requirement for testing
  bypassAccountCheck?: boolean;
}

export default function UnifiedCalendarPage({ 
  tourControls, 
  onLogActivity, 
  onComposeEmail,
  customCalendarComponent,
  contacts,
  onSearchContacts,
  isLoadingContacts,
  bypassAccountCheck = false
}: UnifiedCalendarPageProps) {
  return (
    <CalendarProvider
      dateRangeMonths={2}
      contacts={contacts}
      onSearchContacts={onSearchContacts}
      isLoadingContacts={isLoadingContacts}
    >
      <UnifiedCalendarContent 
        tourControls={tourControls} 
        onLogActivity={onLogActivity}
        onComposeEmail={onComposeEmail}
        customCalendarComponent={customCalendarComponent}
        bypassAccountCheck={bypassAccountCheck}
      />
    </CalendarProvider>
  );
}
